import React from 'react';
import { parse } from 'date-fns';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Confirm from 'components/Confirm';
import { DropdownToggle, DropdownMenu, DropdownItem } from 'components/Dropdown2';
import { CharactersRemaining } from 'components/Form';
import { ButtonGroup, Button, Dropdown, Form, Textarea } from 'components/HookForm';
import InfoBanner, { BANNER_VARIANTS } from 'components/InfoBanner/InfoBanner';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import LINKS from 'constants/links';
import { intlShape, schedulePropTypes } from 'constants/propTypes';
import appMessages from 'features/App/App.messages';
import { bem } from 'helpers/class-name';
import { format12HourTimeLowercase } from 'helpers/intl-fns';
import { selectLocale } from 'selectors/locale-data';
import { selectIsMobile } from 'selectors/ui';

import './CancelScheduleModal.scss';

const messages = {
  button: defineMessages({
    cancel: {
      id: 'cancel-schedule-modal.cancel-single-instance',
      defaultMessage: 'Cancel',
    },
    accept: {
      id: 'cancel-schedule-modal.accept-single-instance',
      defaultMessage: 'Confirm',
    },
  }),
  recurring: defineMessages({
    title: {
      id: 'cancel-schedule-modal.title-recurring',
      defaultMessage: 'Cancel upcoming classes',
    },
    content: {
      id: 'cancel-schedule-modal.content-recurring',
      defaultMessage:
        'Are you sure you want to cancel all upcoming {startDateTime, date, dow-time} {scheduleName} classes from your schedule?',
    },
  }),
  singleInstance: defineMessages({
    title: {
      id: 'cancel-schedule-modal.title-single-instance',
      defaultMessage: 'Confirm cancellation',
    },
    scheduleName: {
      id: 'cancel-schedule-modal.schedule-name',
      defaultMessage: '{formattedTime} | {scheduleName}',
    },
    scheduleDate: {
      id: 'cancel-schedule-modal.schedule-date',
      defaultMessage: '{startDateTime, date, long-w-dow}',
    },
    redirectToManageSchedule: {
      id: 'cancel-schedule-modal.redirect-to-manage-schedule',
      defaultMessage: 'To make changes to all future instances of this class <a>edit the class schedule</a>.',
    },
  }),
  cancellationReason: defineMessages({
    label: {
      id: 'cancel-schedule-modal.cancellation-reason.label',
      defaultMessage: 'Cancellation reason',
    },
    placeholder: {
      id: 'cancel-schedule-modal.cancellation-reason.placeholder',
      defaultMessage: 'Select cancellation reason',
    },
    detailsLabel: {
      id: 'cancel-schedule-modal.cancellation-reason.provide-details-label',
      defaultMessage: 'Please describe cancellation reason',
    },
    detailsPlaceholder: {
      id: 'cancel-schedule-modal.cancellation-reason.provide-details',
      defaultMessage: 'Share why this class is being canceled',
    },
    instructorNoShow: {
      id: 'cancel-schedule-modal.cancellation-reason.instructor-no-show',
      defaultMessage: 'Instructor unwell/no-show',
    },
    overbookedClass: {
      id: 'cancel-schedule-modal.cancellation-reason.overbooked-class',
      defaultMessage: 'Overbooked class',
    },
    lowAttendance: {
      id: 'cancel-schedule-modal.cancellation-reason.low-attendance',
      defaultMessage: 'Low attendance',
    },
    scheduleIncorrect: {
      id: 'cancel-schedule-modal.cancellation-reason.schedule-incorrect',
      defaultMessage: 'Schedule incorrect',
    },
    venueTechnicalIssue: {
      id: 'cancel-schedule-modal.cancellation-reason.venue-technical-issue',
      defaultMessage: 'Venue/Technical issue',
    },
    weatherConditions: {
      id: 'cancel-schedule-modal.cancellation-reason.weather-conditions',
      defaultMessage: 'Weather conditions',
    },
    other: {
      id: 'cancel-schedule-modal.cancellation-reason.other',
      defaultMessage: 'Other',
    },
  }),
  sendMessage: defineMessages({
    label: {
      id: 'cancel-schedule-modal.send-message.label',
      defaultMessage: 'Client message',
    },
    placeholder: {
      id: 'cancel-schedule-modal.send-message.placeholder',
      defaultMessage: 'Share why this class is being canceled',
    },
    legalNotice: {
      id: 'cancel-schedule-modal.send-message.legal-notice',
      defaultMessage: 'By using this feature, you agree to comply with <a>Usage Guidelines</a>.',
    },
  }),
  notice: defineMessages({
    hasReservations: {
      id: 'cancel-schedule-modal.has-reservations',
      defaultMessage: '{classpassBooked, plural, one {# reservation} other {# reservations}} will be canceled',
    },
    reservationCancellationNotice: {
      id: 'cancel-schedule-modal.reservation-cancellation-notice',
      defaultMessage:
        'Clients will receive a refund for the credits used to book this class and you will not receive any payout for this canceled class. This action will not affect future class times.',
    },
  }),
};

const { block, element } = bem('cancel-schedule-modal');
const FORM_NAME = 'cancel-schedule-modal';

const CLIENT_MESSAGE_MAX_INPUT_LENGTH = 2000;

const SCHEDULE_CANCEL_REASONS = {
  INSTRUCTOR_UNWELL_NO_SHOW: 'INSTRUCTOR_UNWELL_NO_SHOW',
  OVERBOOKED_CLASS: 'OVERBOOKED_CLASS',
  LOW_ATTENDANCE: 'LOW_ATTENDANCE',
  SCHEDULE_INCORRECT: 'SCHEDULE_INCORRECT',
  VENUE_TECHNICAL_ISSUE: 'VENUE_TECHNICAL_ISSUE',
  WEATHER_CONDITIONS: 'WEATHER_CONDITIONS',
  OTHER: 'OTHER',
};

const POST_SCHEDULE_CANCEL_REASONS = {
  POST_INSTRUCTOR_UNWELL_NO_SHOW: 'POST_INSTRUCTOR_UNWELL_NO_SHOW',
  POST_OVERBOOKED_CLASS: 'POST_OVERBOOKED_CLASS',
  POST_LOW_ATTENDANCE: 'POST_LOW_ATTENDANCE',
  POST_SCHEDULE_INCORRECT: 'POST_SCHEDULE_INCORRECT',
  POST_VENUE_TECHNICAL_ISSUE: 'POST_VENUE_TECHNICAL_ISSUE',
  POST_WEATHER_CONDITIONS: 'POST_WEATHER_CONDITIONS',
  POST_OTHER: 'POST_OTHER',
};

const SCHEDULE_CANCEL_REASONS_OPTIONS = [
  SCHEDULE_CANCEL_REASONS.INSTRUCTOR_UNWELL_NO_SHOW,
  SCHEDULE_CANCEL_REASONS.OVERBOOKED_CLASS,
  SCHEDULE_CANCEL_REASONS.LOW_ATTENDANCE,
  SCHEDULE_CANCEL_REASONS.SCHEDULE_INCORRECT,
  SCHEDULE_CANCEL_REASONS.VENUE_TECHNICAL_ISSUE,
  SCHEDULE_CANCEL_REASONS.WEATHER_CONDITIONS,
  SCHEDULE_CANCEL_REASONS.OTHER,
];

const POST_SCHEDULE_CANCEL_REASONS_OPTIONS = [
  POST_SCHEDULE_CANCEL_REASONS.POST_INSTRUCTOR_UNWELL_NO_SHOW,
  POST_SCHEDULE_CANCEL_REASONS.POST_OVERBOOKED_CLASS,
  POST_SCHEDULE_CANCEL_REASONS.POST_LOW_ATTENDANCE,
  POST_SCHEDULE_CANCEL_REASONS.POST_SCHEDULE_INCORRECT,
  POST_SCHEDULE_CANCEL_REASONS.POST_VENUE_TECHNICAL_ISSUE,
  POST_SCHEDULE_CANCEL_REASONS.POST_WEATHER_CONDITIONS,
  POST_SCHEDULE_CANCEL_REASONS.POST_OTHER,
];

const SCHEDULE_CANCEL_REASONS_MESSAGES = {
  [SCHEDULE_CANCEL_REASONS.INSTRUCTOR_UNWELL_NO_SHOW]: messages.cancellationReason.instructorNoShow,
  [SCHEDULE_CANCEL_REASONS.OVERBOOKED_CLASS]: messages.cancellationReason.overbookedClass,
  [SCHEDULE_CANCEL_REASONS.LOW_ATTENDANCE]: messages.cancellationReason.lowAttendance,
  [SCHEDULE_CANCEL_REASONS.SCHEDULE_INCORRECT]: messages.cancellationReason.scheduleIncorrect,
  [SCHEDULE_CANCEL_REASONS.VENUE_TECHNICAL_ISSUE]: messages.cancellationReason.venueTechnicalIssue,
  [SCHEDULE_CANCEL_REASONS.WEATHER_CONDITIONS]: messages.cancellationReason.weatherConditions,
  [SCHEDULE_CANCEL_REASONS.OTHER]: messages.cancellationReason.other,
  [POST_SCHEDULE_CANCEL_REASONS.POST_INSTRUCTOR_UNWELL_NO_SHOW]: messages.cancellationReason.instructorNoShow,
  [POST_SCHEDULE_CANCEL_REASONS.POST_OVERBOOKED_CLASS]: messages.cancellationReason.overbookedClass,
  [POST_SCHEDULE_CANCEL_REASONS.POST_LOW_ATTENDANCE]: messages.cancellationReason.lowAttendance,
  [POST_SCHEDULE_CANCEL_REASONS.POST_SCHEDULE_INCORRECT]: messages.cancellationReason.scheduleIncorrect,
  [POST_SCHEDULE_CANCEL_REASONS.POST_VENUE_TECHNICAL_ISSUE]: messages.cancellationReason.venueTechnicalIssue,
  [POST_SCHEDULE_CANCEL_REASONS.POST_WEATHER_CONDITIONS]: messages.cancellationReason.weatherConditions,
  [POST_SCHEDULE_CANCEL_REASONS.POST_OTHER]: messages.cancellationReason.other,
};

const CancelScheduleReasonDropdown = ({ isInThePast, name }) => {
  const dropdownOptions = isInThePast ? POST_SCHEDULE_CANCEL_REASONS_OPTIONS : SCHEDULE_CANCEL_REASONS_OPTIONS;
  return (
    <Dropdown className={element('dropdown')} name={name} required label={messages.cancellationReason.label}>
      {({ value: selectedReason, onChange }) => (
        <>
          <DropdownToggle>
            {!selectedReason ? (
              <Message message={messages.cancellationReason.placeholder} className="text--light" />
            ) : (
              <Message id={selectedReason} messages={SCHEDULE_CANCEL_REASONS_MESSAGES} />
            )}
          </DropdownToggle>
          <DropdownMenu className={element('dropdown-menu')}>
            {dropdownOptions.map(reason => (
              <DropdownItem key={reason} isSelected={reason === selectedReason} onClick={() => onChange(reason)}>
                <Message message={SCHEDULE_CANCEL_REASONS_MESSAGES[reason]} />
              </DropdownItem>
            ))}
          </DropdownMenu>
        </>
      )}
    </Dropdown>
  );
};

CancelScheduleReasonDropdown.propTypes = {
  isInThePast: PropTypes.bool,
  name: PropTypes.string,
};

const CancelScheduleModal = ({ activeSchedule, cancelSchedule, isRecurring, intl, msiScheduleLink }) => {
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);
  const isMobile = useSelector(selectIsMobile);

  const { isInThePast, resource, scheduleName, spots, startTime, startDateTime } = activeSchedule;
  const { resourceName } = resource || {};
  const { classpassBooked, totalBooked } = spots || {};

  const closeAriaLabel = intl.formatMessage(appMessages.close);
  const parsedTime = startTime ? parse(startTime, 'HH:mm', new Date()) : undefined;

  const hasReservations = totalBooked ? totalBooked > 0 : classpassBooked > 0;
  const shouldDisplayMessageForm = !isRecurring && hasReservations;

  const handleSubmit = formValues => {
    const { additionalDetails, cancellationReason, clientMessage } = formValues;
    cancelSchedule(additionalDetails, cancellationReason, clientMessage);
  };

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  return (
    <Confirm className={block()} isOpen size="complex">
      {() => (
        <Form
          id={FORM_NAME}
          defaultValues={{
            additionalDetails: '',
            cancellationReason: '',
            clientMessage: '',
          }}
          onSubmit={handleSubmit}>
          {({ formState, watch }) => {
            const { isSubmitting } = formState;
            const [cancellationReason, clientMessage] = watch(['cancellationReason', 'clientMessage']);
            const isCancellationReasonOther = [
              SCHEDULE_CANCEL_REASONS.OTHER,
              POST_SCHEDULE_CANCEL_REASONS.POST_OTHER,
            ].includes(cancellationReason);

            return (
              <>
                <div className={element('wrapper')}>
                  <div className={element('title-wrapper')}>
                    <Message message={isRecurring ? messages.recurring.title : messages.singleInstance.title} />
                    <button className={element('close-button')} type="button" onClick={handleCloseModal}>
                      <Isvg src={ICONS.cross} aria-label={closeAriaLabel} />
                    </button>
                  </div>
                  <div className={element('schedule-detail-wrapper')}>
                    {isRecurring ? (
                      <Message
                        message={messages.recurring.content}
                        values={{
                          scheduleName,
                          startDateTime,
                        }}
                      />
                    ) : (
                      <>
                        <Message
                          className="text--semibold"
                          message={messages.singleInstance.scheduleName}
                          values={{
                            formattedTime: format12HourTimeLowercase(parsedTime, locale),
                            scheduleName,
                          }}
                        />
                        <span>{resourceName}</span>
                        <Message
                          message={messages.singleInstance.scheduleDate}
                          values={{
                            startDateTime,
                          }}
                        />
                      </>
                    )}
                  </div>
                  <When predicate={!isRecurring && !!msiScheduleLink}>
                    <Message
                      message={messages.singleInstance.redirectToManageSchedule}
                      values={{
                        a: text => <a href={msiScheduleLink}>{text}</a>,
                      }}
                    />
                  </When>
                  <CancelScheduleReasonDropdown isInThePast={isInThePast} name="cancellationReason" />
                  <When predicate={isCancellationReasonOther}>
                    <Textarea
                      required={isCancellationReasonOther}
                      showRequiredLabel
                      label={messages.cancellationReason.detailsLabel}
                      name="additionalDetails"
                      heightInLines={2}
                      placeholder={messages.cancellationReason.detailsPlaceholder}
                    />
                  </When>
                  <When predicate={shouldDisplayMessageForm}>
                    <div className={element('client-message-wrapper')}>
                      <Textarea
                        heightInLines={isMobile ? 2 : 8}
                        optional
                        label={messages.sendMessage.label}
                        name="clientMessage"
                        placeholder={messages.sendMessage.placeholder}
                        maxLength={CLIENT_MESSAGE_MAX_INPUT_LENGTH}
                      />
                      <CharactersRemaining
                        className="text--sm text--ui70"
                        length={CLIENT_MESSAGE_MAX_INPUT_LENGTH}
                        value={clientMessage}
                      />
                      <Message
                        message={messages.sendMessage.legalNotice}
                        values={{
                          a: text => (
                            <a href={LINKS.userMessagingUsageGuidelines.href} target="_blank" rel="noopener noreferrer">
                              {text}
                            </a>
                          ),
                        }}
                      />
                    </div>
                    <InfoBanner
                      className={element('info-notice')}
                      icon={ICONS.warningYellow}
                      variant={BANNER_VARIANTS.YELLOW}
                      hideCTA>
                      <div className={element('info-notice-content')}>
                        <Message
                          className="text--bold"
                          message={messages.notice.hasReservations}
                          values={{ classpassBooked }}
                        />
                        <Message message={messages.notice.reservationCancellationNotice} />
                      </div>
                    </InfoBanner>
                  </When>
                </div>
                <ButtonGroup className={element('button-group')}>
                  <Message
                    as={Button}
                    styled="tertiary"
                    message={messages.button.cancel}
                    type={Button.TYPES.BUTTON}
                    onClick={handleCloseModal}
                  />
                  <Message
                    className={element('submit-btn')}
                    as={Button}
                    styled="primary"
                    message={messages.button.accept}
                    type={Button.TYPES.SUBMIT}
                    waiting={isSubmitting}
                  />
                </ButtonGroup>
              </>
            );
          }}
        </Form>
      )}
    </Confirm>
  );
};

CancelScheduleModal.propTypes = {
  isRecurring: PropTypes.bool,
  cancelSchedule: PropTypes.func.isRequired,
  intl: intlShape,
  ...schedulePropTypes,
};

export default CancelScheduleModal;
